import type { Dispatch } from 'redux'
import type { EmailStatus } from '@disco/types/types/brief'
import type { BadgeProps } from '@radix-ui/themes'
import React from 'react'
import { IoReload } from '@react-icons/all-files/io5/IoReload'
import { IoEllipsisHorizontal } from '@react-icons/all-files/io5/IoEllipsisHorizontal'
import { MdReply } from '@react-icons/all-files/md/MdReply'
import dayjs from '@disco/utils/dayjs'
import {
  Badge,
  DropdownMenu,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from 'components/ds'
import { toggleRecipientBriefLink } from '@disco/state-management/reducers/entities/briefs'
import { pushUINotification } from '@disco/utils/notifications'
import { RowSelectionState } from '@disco/types/types/data-table'
import styles from './tableConfig.module.css'
import mixpanel from '@disco/api/mixpanel'

const dateFormat = 'DD/MM/YYYY'

const VALID_STATUSES = [
  'Sent',
  'Delivered',
  'Opened',
  'Clicked',
  'Bounced',
  'Undeliverable',
  'Unsubscribed',
] as const satisfies readonly EmailStatus[]

const COLOR_MAP: Record<(typeof VALID_STATUSES)[number], BadgeProps['color']> =
  {
    Sent: 'blue',
    Delivered: 'green',
    Opened: 'green',
    Clicked: 'green',
    Bounced: 'amber',
    Undeliverable: 'red',
    Unsubscribed: 'red',
  }

// TODO: Add in a link for 'Learn more' once we have it in the tooltip.
const TOOLTIP_TEXT: Record<(typeof VALID_STATUSES)[number], string> = {
  Sent: 'The email has left our system and is on its way.',
  Delivered: "The email successfully reached the recipient's inbox.",
  Opened: 'The recipient has opened the email.',
  Clicked: 'The recipient clicked the brief link within the email.',
  Bounced:
    "Delivery failed. This usually happens if the email address is invalid or the recipient's inbox is blocked.",
  Undeliverable:
    "Delivery failed. This usually happens if the email address is invalid or the recipient's inbox is blocked.",
  Unsubscribed:
    'This recipient reported the email as spam and has opted out of receiving briefs.',
}

export const isValidStatus = (
  status: string,
): status is (typeof VALID_STATUSES)[number] =>
  (VALID_STATUSES as readonly string[]).includes(status)

export const EmailStatusCell = ({
  emailStatus,
}: {
  emailStatus?: EmailStatus | null
}) => {
  let validatedEmailStatus
  if (!emailStatus || !isValidStatus(emailStatus)) {
    validatedEmailStatus = 'Sent'
  } else {
    validatedEmailStatus = emailStatus
  }

  return (
    <Tooltip content={TOOLTIP_TEXT[validatedEmailStatus]}>
      <Badge variant="outline" color={COLOR_MAP[validatedEmailStatus]}>
        {validatedEmailStatus}
      </Badge>
    </Tooltip>
  )
}

const ShareCell = ({
  recipients = [],
  disabled,
  onShareWithRecipients,
}: {
  briefId: number
  recipients: string[]
  disabled?: boolean
  onShareWithRecipients: (emails: string[]) => void
}) => {
  const handleShare = () => {
    onShareWithRecipients(recipients)
  }

  return (
    <Flex justify="center" width="100%">
      <IconButton
        variant="ghost"
        onClick={handleShare}
        aria-label="Share brief"
        disabled={disabled}
      >
        <MdReply style={{ transform: 'scaleX(-1)' }} />
      </IconButton>
    </Flex>
  )
}

const ActionsCell = ({
  briefId,
  contactId,
  savedRecipientsId,
  submissionUrl,
  linkEnabled,
  dispatch,
  rowSelection,
  setRowSelection,
}: {
  briefId: number
  contactId: number
  savedRecipientsId: number | null
  dispatch: Dispatch<any>
  linkEnabled: boolean
  submissionUrl: string
  rowSelection: RowSelectionState
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>
}) => {
  const handleToggleLink = (enabled: boolean) => {
    if (!enabled && rowSelection[contactId]) {
      setRowSelection(prevSelection => {
        const newSelection = { ...prevSelection }
        delete newSelection[contactId]
        return newSelection
      })
    }

    dispatch(
      toggleRecipientBriefLink({
        briefId,
        recipients: [
          {
            contact_id: contactId,
            saved_recipients_id: savedRecipientsId,
            enabled,
          },
        ],
      }),
    )
    mixpanel.sendPureEvent('Brief recipient link toggled', {
      Context: 'Briefs',
      brief_id: briefId,
      bulk: false,
      enabled: enabled,
    })
  }

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(submissionUrl)
    pushUINotification({
      message: `Submission link copied!`,
      level: 'success',
    })
  }

  const enableButton = (
    <IconButton
      onClick={() => handleToggleLink(true)}
      variant="ghost"
      aria-label="Enable link"
    >
      <IoReload />
    </IconButton>
  )

  const actionButton = (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton variant="ghost" aria-label="Actions">
          <IoEllipsisHorizontal />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <DropdownMenu.Item onSelect={handleCopyLink}>
          Copy link
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => handleToggleLink(false)}>
          Disable link
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )

  return (
    <Flex justify="center" width="100%">
      {linkEnabled ? actionButton : enableButton}
    </Flex>
  )
}

const disabledStyles = row =>
  (!row?.original.link_enabled || row?.original.isUpdating) && {
    color: '#a5adba',
  }

const createColumns = (
  dispatch: Dispatch,
  onShareWithRecipients: (emails: string[]) => void,
  rowSelection: RowSelectionState,
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>,
) => [
  {
    header: 'Name',
    cell: ({ row }) => (
      <Text style={disabledStyles(row)} className={styles.truncatedName}>
        {row.original.name}
      </Text>
    ),
    id: 'name',
  },
  {
    header: 'Email Sent',
    cell: ({ row }) =>
      row.original.last_sent ? (
        <Text style={disabledStyles(row)}>
          {dayjs(row.original.last_sent).format(dateFormat)}
        </Text>
      ) : null,
    id: 'last_sent',
    size: 100,
  },
  {
    header: 'Email Status',
    cell: ({ row }) => (
      <EmailStatusCell emailStatus={row.original.email_status} />
    ),
    id: 'email_status',
    size: 133,
  },
  {
    header: 'Brief Viewed',
    cell: ({ row }) =>
      row.original.last_viewed ? (
        <Text style={disabledStyles(row)}>
          {dayjs(row.original.last_viewed).format(dateFormat)}
        </Text>
      ) : null,
    id: 'last_viewed',
    size: 100,
  },
  {
    header: 'Brief Submission',
    cell: ({ row }) =>
      row.original.last_submission ? (
        <Text style={disabledStyles(row)}>
          {dayjs(row.original.last_submission).format(dateFormat)}
        </Text>
      ) : null,
    id: 'last_submission',
    size: 100,
  },
  {
    header: () => (
      <Flex justify="center" width="100%">
        Share
      </Flex>
    ),
    id: 'share',
    cell: ({ row }) => (
      <ShareCell
        briefId={row.original.briefId}
        onShareWithRecipients={onShareWithRecipients}
        recipients={[row.original.email]}
        disabled={!row.original.link_enabled}
      />
    ),
    accessorFn: () => null,
    size: 80,
  },
  {
    header: () => (
      <Flex justify="center" width="100%">
        Link
      </Flex>
    ),
    id: 'link',
    cell: ({ row }) => (
      <ActionsCell
        briefId={row.original.briefId}
        contactId={row.original.contact_id}
        savedRecipientsId={row.original.saved_recipients_id}
        submissionUrl={row.original.brief_submission_url}
        dispatch={dispatch}
        linkEnabled={row.original.link_enabled}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
      />
    ),
    accessorFn: () => null,
    size: 80,
  },
]

export default createColumns
